// Cierre de la venta del paso 09: fija las piezas con /pieces y cierra con complete-sale. Con la
// sesión marcada (piecesHeldAt) el cierre va sin lines; sin la marca, con las lines de siempre.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useCompleteSale, usePinPieces } from '../../api/useWizard';
import { completeSaleFailure, type CompleteSaleFailure } from '../../completeSaleErrors';
import { pinFailureAboutPieces, pinPiecesFailure, type PinPiecesFailure } from '../../piecesErrors';
import {
    allPiecesCaptured,
    completeSaleLinesOf,
    duplicatedPieceKeys,
    financedPhoneOf,
    pieceKeysOf,
    piecesToCapture,
    pinPiecesBody,
    type CapturedIdentifiers,
    type DeliveryPiece,
} from '../../deliveryPieces';
import type { CompleteSaleLine, WizardSession } from '../../types';

const DUPLICATED_MESSAGE = 'Ya capturaste este identificador en otra pieza.';
const PIECES_STATUS = 'Corrige las piezas señaladas y vuelve a intentar.';

export interface SaleClosing {
    pieces: DeliveryPiece[];
    captured: CapturedIdentifiers;
    setPiece: (key: string, value: string) => void;
    fieldErrors: Record<string, string>;
    piecesReady: boolean;
    /** El vendedor dio por buenas las piezas; editar una lo deshace. */
    piecesConfirmed: boolean;
    confirmPieces: () => void;
    /** Fija con /pieces lo capturado (y el IMEI del financiado); llama `onPinned` si quedó. */
    pin: (financedImei?: string, onPinned?: () => void) => void;
    pinning: boolean;
    pinFailure: PinPiecesFailure | null;
    clearPinFailure: () => void;
    saleRef: string | undefined;
    failure: CompleteSaleFailure | null;
    /** Lo que se lee junto al estado de la venta; corto si el error ya quedó en una pieza. */
    statusError: string | null;
    closing: boolean;
    close: () => void;
    /** Reintento del último fallo; ausente si repetir la llamada no lo arregla. */
    retry: (() => void) | undefined;
}

interface PinAttempt {
    financedImei?: string;
    onPinned?: () => void;
}

/**
 * `withFinancedPhone`: el IMEI del teléfono financiado se captura aparte, en el enrolamiento.
 * `canClose`: además de las piezas, lo que la pista exige para cerrar sola.
 * `pinOnConfirm`: confirmar las piezas las fija con /pieces (contado).
 * `closedSaleId`: venta que ya cerró el servidor (poll del candado).
 */
export function useSaleClosing(
    sessionId: string,
    session: WizardSession | undefined,
    refresh: () => Promise<unknown>,
    {
        withFinancedPhone,
        canClose,
        pinOnConfirm = false,
        closedSaleId,
    }: {
        withFinancedPhone: boolean;
        canClose: boolean;
        pinOnConfirm?: boolean;
        closedSaleId?: string;
    },
): SaleClosing {
    const navigate = useNavigate();
    const completeSale = useCompleteSale(sessionId);
    const pinPieces = usePinPieces(sessionId);
    const pieces = useMemo(
        () => piecesToCapture(session, withFinancedPhone ? financedPhoneOf(session) : undefined),
        [session, withFinancedPhone],
    );
    const [captured, setCaptured] = useState<CapturedIdentifiers>({});
    const [flagged, setFlagged] = useState<ReadonlySet<string>>(new Set());
    const [confirmed, setConfirmed] = useState(false);
    const [reloading, setReloading] = useState(false);
    const [pinning, setPinning] = useState(false);
    const [pinFailure, setPinFailure] = useState<PinPiecesFailure | null>(null);
    // Lines de complete-sale de lo ya fijado, por si la sesión quedó sin la marca.
    const [pinnedLines, setPinnedLines] = useState<CompleteSaleLine[] | undefined>();
    const [heldByPin, setHeldByPin] = useState(false);
    const lastPin = useRef<PinAttempt>({});

    const piecesReady = allPiecesCaptured(pieces, captured);
    // Sin piezas no hay nada que confirmar: se cierra como antes de las líneas.
    const piecesConfirmed = pieces.length === 0 || (confirmed && piecesReady);
    const saleRef = session?.saleId ?? closedSaleId ?? completeSale.data?.saleId;
    const rawFailure = completeSale.isError ? completeSaleFailure(completeSale.error) : null;
    const failure = rawFailure && reloading ? { ...rawFailure, canRetry: false } : rawFailure;

    const reloadSession = (): void => {
        setReloading(true);
        setConfirmed(false);
        void Promise.resolve(refresh())
            .catch(() => undefined)
            .finally(() => {
                setReloading(false);
            });
    };

    const close = (): void => {
        const lines =
            session?.piecesHeldAt || heldByPin
                ? undefined
                : (pinnedLines ?? completeSaleLinesOf(pieces, captured));
        void completeSale
            .mutateAsync(lines)
            .then(refresh)
            .catch((error: unknown) => {
                const failed = completeSaleFailure(error);
                if (failed.backToBuildSale) {
                    toast.error(failed.message);
                    void navigate({
                        to: '/wizard/s/$sessionId/$step',
                        params: { sessionId, step: 'build-sale' },
                    });
                    return;
                }
                setFlagged(new Set(failed.target ? pieceKeysOf(pieces, failed.target) : []));
                // Reintentar con la sesión vieja mandaría el mismo body: se relee antes.
                if (failed.reloadSession) reloadSession();
            });
    };

    const pin = (financedImei?: string, onPinned?: () => void): void => {
        lastPin.current = { financedImei, onPinned };
        const body = pinPiecesBody(session, pieces, captured, financedImei);
        const lines = completeSaleLinesOf(pieces, captured);
        const done = (): void => {
            setConfirmed(true);
            onPinned?.();
        };
        if (!body) {
            done();
            return;
        }
        setPinning(true);
        setPinFailure(null);
        void pinPieces
            .mutateAsync(body)
            .then((updated) => {
                setPinnedLines(lines);
                setHeldByPin(!!updated.piecesHeldAt);
                done();
            })
            .catch((error: unknown) => {
                const failed = pinPiecesFailure(error);
                setPinFailure(failed);
                setFlagged(new Set(failed.target ? pieceKeysOf(pieces, failed.target) : []));
                if (failed.action === 'reload') reloadSession();
            })
            .finally(() => {
                setPinning(false);
            });
    };

    useEffect(() => {
        if (session && canClose && piecesConfirmed && !saleRef) close();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.sessionId, canClose, piecesConfirmed, saleRef]);

    const setPiece = (key: string, value: string): void => {
        setCaptured((current) => ({ ...current, [key]: value }));
        setConfirmed(false);
        setPinnedLines(undefined);
        setFlagged((current) => new Set([...current].filter((k) => k !== key)));
    };

    const flaggedMessage = pinFailure?.message ?? failure?.message ?? PIECES_STATUS;
    const fieldErrors: Record<string, string> = {};
    for (const key of duplicatedPieceKeys(pieces, captured)) fieldErrors[key] = DUPLICATED_MESSAGE;
    for (const key of flagged) fieldErrors[key] = flaggedMessage;

    let statusError: string | null = null;
    if (pinFailure) {
        statusError = pinFailureAboutPieces(pinFailure) ? PIECES_STATUS : pinFailure.message;
    } else if (failure) statusError = failure.aboutPieces ? PIECES_STATUS : failure.message;

    let retry: (() => void) | undefined;
    if (pinFailure) {
        if (pinFailure.action === 'retry') {
            retry = () => {
                pin(lastPin.current.financedImei, lastPin.current.onPinned);
            };
        }
    } else if (failure?.canRetry !== false) {
        retry = close;
    }

    return {
        pieces,
        captured,
        setPiece,
        fieldErrors,
        piecesReady,
        piecesConfirmed,
        confirmPieces: () => {
            if (!piecesReady) return;
            if (pinOnConfirm) pin();
            else setConfirmed(true);
        },
        pin,
        pinning,
        pinFailure,
        clearPinFailure: () => {
            setPinFailure(null);
        },
        saleRef,
        failure,
        statusError,
        closing: completeSale.isPending,
        close,
        retry,
    };
}
